import React from "react";
import { getImageUrl } from "utils/imageHelper";

export default function ViewEventDetails({ data }) {
  if (!data) return null;

  const resolveMedia = (mediaPath) => {
    if (!mediaPath) return null;
    return typeof mediaPath === "string" ? getImageUrl(mediaPath) : null;
  };

  const isActive =
    data.status?.toLowerCase() === "active" || data.status === true;

  const formatDateTime = (isoStr) => {
    if (!isoStr) return "N/A";
    try {
      const date = new Date(isoStr);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return isoStr;
    }
  };

  // Resolve default media URL & type
  let defaultMediaUrl = null;
  let defaultMediaType = "image";

  if (data.default_media) {
    const meta =
      typeof data.default_media === "string"
        ? (() => {
            try {
              return JSON.parse(data.default_media);
            } catch {
              return { value: data.default_media, mediaType: "image" };
            }
          })()
        : data.default_media;

    defaultMediaUrl = resolveMedia(meta?.value || meta?.path || meta);
    defaultMediaType = meta?.mediaType || meta?.type || "image";
  }

  const galleryImages = Array.isArray(data.eventImage)
    ? data.eventImage.map(resolveMedia).filter(Boolean)
    : [];

  const galleryVideos = Array.isArray(data.eventVideo)
    ? data.eventVideo.map(resolveMedia).filter(Boolean)
    : [];

  const dates = Array.isArray(data.event_dates) ? data.event_dates : [];
  const exhibitors = Array.isArray(data.exhibitors) ? data.exhibitors : [];

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">
      {/* SECTION 1: EVENT DETAILS */}
      <ViewSection title="Event Details">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox label="Event Name" value={data.event_name} />

          <ViewDisplayBox
            label="Event Category"
            value={data.event_category_id?.title || "N/A"}
          />

          <ViewDisplayBox
            label="Location"
            value={data.location}
            className="md:col-span-2"
          />

          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Description</span>
            <div
              className="view-value"
              dangerouslySetInnerHTML={{
                __html: data.description || "N/A",
              }}
            />
          </div>
        </div>
      </ViewSection>

      {/* SECTION 2: SCHEDULE & DATES */}
      {dates.length > 0 && (
        <ViewSection title="Event Schedule & Dates">
          <div className="space-y-3">
            {dates.map((dateSlot, idx) => (
              <div
                key={dateSlot._id || idx}
                className="rounded-xl border border-gray-200 bg-gray-50/50 p-4"
              >
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  Slot #{idx + 1}
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <ViewDisplayBox
                    label="Start Date & Time"
                    value={formatDateTime(dateSlot.from_date)}
                  />
                  <ViewDisplayBox
                    label="End Date & Time"
                    value={formatDateTime(dateSlot.to_date)}
                  />
                </div>
              </div>
            ))}
          </div>
        </ViewSection>
      )}

      {/* SECTION 3: EXHIBITORS & STALLS */}
      {exhibitors.length > 0 && (
        <ViewSection title="Assigned Exhibitors">
          <div className="space-y-3">
            {exhibitors.map((ex, idx) => (
              <div
                key={ex._id || idx}
                className="rounded-xl border border-gray-200 bg-gray-50/50 p-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                  <ViewDisplayBox
                    label="Exhibitor"
                    value={ex.exhibitor_id?.title || ex.exhibitor_name || "N/A"}
                  />
                  <ViewDisplayBox
                    label="Pavilion"
                    value={ex.pavilion_id?.pavilion_name || "N/A"}
                  />
                  <ViewDisplayBox label="Stall No" value={ex.stall_no} />
                  <ViewDisplayBox label="Floor No" value={ex.floor_no} />
                </div>
              </div>
            ))}
          </div>
        </ViewSection>
      )}

      {/* SECTION 4: STATUS */}
      <ViewSection title="Status">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="view-label">Status</span>
            <div className="view-value flex items-center">
              <span>{isActive ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 5: EVENT MEDIA (PRODUCT VIEW PATTERN) */}
      <ViewSection title="Event Media">
        <div className="flex flex-col gap-4">
          {/* Featured / Default Media */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Default Media</span>
            <MediaContainer
              src={defaultMediaUrl}
              type={defaultMediaType}
              alt="Default Event Media"
              placeholder="No Default Media Set"
              height="h-36"
            />
          </div>

          {/* Gallery Images */}
          {galleryImages.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="view-label">Gallery Images</span>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {galleryImages.map((url, idx) => (
                  <MediaContainer
                    key={idx}
                    src={url}
                    type="image"
                    alt={`Event Image ${idx + 1}`}
                    placeholder="No Preview"
                    height="h-28"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Gallery Videos */}
          {galleryVideos.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="view-label">Gallery Videos</span>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {galleryVideos.map((url, idx) => (
                  <MediaContainer
                    key={idx}
                    src={url}
                    type="video"
                    alt={`Event Video ${idx + 1}`}
                    placeholder="No Preview"
                    height="h-36"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </ViewSection>
    </div>
  );
}

/* =========================================================
   REUSABLE SECTION & BOX COMPONENTS
========================================================= */

function ViewSection({ title, children }) {
  return (
    <div className="view-card flex flex-col gap-6 text-sm">
      <div>
        <div className="view-subtitle">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ViewDisplayBox({ label, value, className = "" }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="view-label">{label}</span>
      <div className="view-value">{value || "N/A"}</div>
    </div>
  );
}

/* =========================================================
   MEDIA CONTAINER (IMAGE & VIDEO HANDLER)
========================================================= */

function MediaContainer({
  src,
  type = "image",
  alt,
  placeholder,
  height = "h-28",
}) {
  return (
    <div className={`${height} view-image-div`}>
      {src ? (
        type === "video" ? (
          <video
            src={src}
            controls
            className="h-full w-full object-cover rounded-lg"
          />
        ) : (
          <img
            src={src}
            alt={alt || "Media preview"}
            className="max-h-full max-w-full object-contain"
          />
        )
      ) : (
        <span className="text-xs text-gray-400">{placeholder}</span>
      )}
    </div>
  );
}